import { cloudConfigured, supabase } from './supabase'

const phaseToApp = { before_work: '業務前', after_work: '業務後' }
const phaseToDb = { 業務前: 'before_work', 業務後: 'after_work' }
const methodToApp = { image_ocr: '画像読取', manual: '手入力' }
const methodToDb = { 画像読取: 'image_ocr', 手入力: 'manual' }
const confirmationToApp = { pending: '未確認', face_to_face: '対面', video_call: 'ビデオ通話', voice_call: '電話・音声通話' }
const confirmationToDb = { 未確認: 'pending', 対面: 'face_to_face', ビデオ通話: 'video_call', '電話・音声通話': 'voice_call' }
const integrationMethodToApp = { unset: '未設定', rest_api: 'REST API', webhook: 'Webhook', csv_import: 'CSV取込', csv_export: 'CSV出力' }
const integrationMethodToDb = Object.fromEntries(Object.entries(integrationMethodToApp).map(([key, value]) => [value, key]))

function requireCloud() {
  if (!cloudConfigured || !supabase) throw new Error('Supabaseの接続情報が設定されていません')
}

function businessParts(isoString) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(isoString)).map((part) => [part.type, part.value]))
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` }
}

function resultOrThrow(result, message) {
  if (result.error) throw new Error(`${message}: ${result.error.message}`)
  return result.data
}

async function fetchAllAlcoholChecks() {
  const pageSize = 1000
  const rows = []
  for (let from = 0; ; from += pageSize) {
    const result = await supabase
      .from('alcohol_checks')
      .select('id, public_id, driver_id, vehicle_id, measured_at, phase, input_method, confirmation_method, value_mg_l, alcohol_detected, visual_check, confirmer_name, checker_identifier, instruction, note, pre_drive_check, status, image_path, created_at')
      .order('measured_at', { ascending: false })
      .range(from, from + pageSize - 1)
    if (result.error) return result
    rows.push(...(result.data || []))
    if ((result.data || []).length < pageSize) return { data: rows, error: null }
  }
}

export async function signInCloud(email, password) {
  requireCloud()
  resultOrThrow(await supabase.auth.signInWithPassword({ email, password }), 'ログインできませんでした')
  const claims = await supabase.auth.getClaims()
  if (claims.error || !claims.data?.claims?.sub) throw new Error('ログイン情報を確認できませんでした')
  return loadCloudData()
}

export async function restoreCloudSession() {
  if (!cloudConfigured || !supabase) return null
  const claims = await supabase.auth.getClaims()
  if (claims.error || !claims.data?.claims?.sub) return null
  return loadCloudData()
}

export async function signOutCloud() {
  if (supabase) await supabase.auth.signOut()
}

export async function loadCloudData() {
  requireCloud()
  const profile = resultOrThrow(await supabase.from('profiles').select('user_id, organization_id, driver_id, role, display_name').single(), '利用者情報を取得できませんでした')
  const currentDate = businessParts(new Date().toISOString()).date
  const [organizationResult, driversResult, vehiclesResult, assignmentsResult, checksResult, integrationsResult, remindersResult] = await Promise.all([
    supabase.from('organizations').select('id, name, office_name, retention_years, warning_only').eq('id', profile.organization_id).single(),
    supabase.from('drivers').select('id, public_id, employee_code, full_name, branch, active').order('employee_code'),
    supabase.from('vehicles').select('id, public_id, registration_number, vehicle_type, model, branch, active').order('registration_number'),
    supabase.from('driver_vehicle_assignments').select('id, assignment_date, driver_id, vehicle_id, assignment_external_id').eq('assignment_date', currentDate),
    fetchAllAlcoholChecks(),
    profile.role === 'admin' ? supabase.from('integration_settings').select('system_type, connection_method, endpoint_url, enabled, last_sync_at') : Promise.resolve({ data: [], error: null }),
    supabase.from('reminder_events').select('id, driver_id, check_date, phase, status, message, created_at').in('status', ['pending', 'sent']).order('created_at', { ascending: false }).limit(100),
  ])
  const organization = resultOrThrow(organizationResult, '組織情報を取得できませんでした')
  const driverRows = resultOrThrow(driversResult, 'ドライバーを取得できませんでした') || []
  const vehicleRows = resultOrThrow(vehiclesResult, '車両を取得できませんでした') || []
  const assignmentRows = resultOrThrow(assignmentsResult, '配車情報を取得できませんでした') || []
  const checkRows = resultOrThrow(checksResult, 'チェック記録を取得できませんでした') || []
  const integrationRows = resultOrThrow(integrationsResult, '連携設定を取得できませんでした') || []
  const reminders = resultOrThrow(remindersResult, '警告情報を取得できませんでした') || []
  const vehicleByDbId = new Map(vehicleRows.map((item) => [item.id, item]))
  const driverByDbId = new Map(driverRows.map((item) => [item.id, item]))
  const vehicles = vehicleRows.map((item) => ({
    id: item.public_id, dbId: item.id, number: item.registration_number, type: item.vehicle_type, model: item.model, branch: item.branch, active: item.active,
  }))
  const drivers = driverRows.map((item) => ({
    id: item.public_id,
    dbId: item.id,
    name: item.full_name,
    employeeId: item.employee_code,
    branch: item.branch,
    active: item.active,
    vehicleIds: assignmentRows.filter((assignment) => assignment.driver_id === item.id).map((assignment) => vehicleByDbId.get(assignment.vehicle_id)?.public_id).filter(Boolean),
  }))
  const records = checkRows.map((item) => {
    const { date, time } = businessParts(item.measured_at)
    return {
      id: item.public_id, dbId: item.id, date, time, phase: phaseToApp[item.phase],
      driverId: driverByDbId.get(item.driver_id)?.public_id, vehicleId: vehicleByDbId.get(item.vehicle_id)?.public_id,
      value: Number(item.value_mg_l), method: methodToApp[item.input_method], presence: item.alcohol_detected,
      visualCheck: item.visual_check, confirmationMethod: confirmationToApp[item.confirmation_method], checkerId: item.checker_identifier,
      confirmer: item.confirmer_name, instruction: item.instruction, note: item.note, preDriveCheck: item.pre_drive_check, status: item.status,
      imagePath: item.image_path, createdAt: item.created_at, audit: [`${time} クラウド記録を取得`],
    }
  })
  const integrationDefaults = {
    attendance: { method: '未設定', enabled: false }, dispatch: { method: '未設定', enabled: false }, fleet: { method: '未設定', enabled: false },
    gatebell: { method: '未設定', enabled: false }, maintenance: { method: '未設定', enabled: false },
  }
  const integrations = integrationRows.reduce((current, item) => ({
    ...current,
    [item.system_type]: { method: integrationMethodToApp[item.connection_method] || '未設定', enabled: item.enabled, endpointUrl: item.endpoint_url, lastSync: item.last_sync_at },
  }), integrationDefaults)
  const session = {
    role: profile.role,
    mode: 'cloud',
    userId: profile.role === 'driver' ? driverByDbId.get(profile.driver_id)?.public_id : 'cloud-admin',
    displayName: profile.display_name,
    organizationId: profile.organization_id,
    driverDbId: profile.driver_id,
  }
  return {
    session, drivers, vehicles, records, reminders,
    settings: {
      companyName: organization.name,
      officeName: organization.office_name,
      retentionYears: organization.retention_years,
      warningOnly: organization.warning_only,
      threshold: 0,
      businessUseCategory: '白ナンバー／自家用貨物（資材配送）',
      checkerModel: '未選定',
      defaultCheckerId: 'ALC-TOKYO-01',
      checkerOperationalStatus: '未設定',
      webhookUrl: '',
      webhookEnabled: false,
      apiKeyLabel: 'SUPABASE-RLS',
      integrations,
    },
  }
}

async function uploadImage(record, organizationId, driverDbId) {
  if (!record.imageDataUrl) return null
  const blob = await (await fetch(record.imageDataUrl)).blob()
  const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg'
  const path = `${organizationId}/${driverDbId}/${record.id}.${extension}`
  resultOrThrow(await supabase.storage.from('alcohol-check-images').upload(path, blob, { contentType: blob.type, upsert: false }), '画像を保存できませんでした')
  return path
}

export async function saveCloudRecord(record, context) {
  requireCloud()
  const driver = context.drivers.find((item) => item.id === record.driverId)
  const vehicle = context.vehicles.find((item) => item.id === record.vehicleId)
  if (!driver?.dbId || !vehicle?.dbId) throw new Error('クラウド上のドライバーまたは車両を特定できません')
  const imagePath = await uploadImage(record, context.organizationId, driver.dbId)
  const payload = {
    organization_id: context.organizationId,
    driver_id: driver.dbId,
    vehicle_id: vehicle.dbId,
    measured_at: record.createdAt,
    phase: phaseToDb[record.phase],
    input_method: methodToDb[record.method],
    confirmation_method: confirmationToDb[record.confirmationMethod],
    value_mg_l: record.value,
    alcohol_detected: record.presence,
    visual_check: record.visualCheck,
    confirmer_name: record.confirmer,
    checker_identifier: record.checkerId,
    instruction: record.instruction,
    note: record.note,
    pre_drive_check: record.preDriveCheck,
    status: record.status,
    image_path: imagePath,
  }
  return resultOrThrow(await supabase.from('alcohol_checks').insert(payload).select('id, public_id').single(), 'チェック記録をクラウドへ保存できませんでした')
}

export async function confirmCloudRecord(record) {
  requireCloud()
  return resultOrThrow(await supabase.from('alcohol_checks').update({
    confirmation_method: confirmationToDb[record.confirmationMethod], visual_check: record.visualCheck,
    confirmer_name: record.confirmer, instruction: record.instruction, note: record.note,
    alcohol_detected: record.presence, status: record.status,
  }).eq('public_id', record.id).select('id').single(), '管理者確認を保存できませんでした')
}

export async function saveCloudDriver(item, organizationId, vehicles, assignmentDate) {
  requireCloud()
  const driver = resultOrThrow(await supabase.from('drivers').insert({
    organization_id: organizationId, employee_code: item.employeeId, full_name: item.name, branch: item.branch, active: true,
  }).select('id, public_id').single(), 'ドライバーを保存できませんでした')
  const assignments = item.vehicleIds.map((vehicleId) => vehicles.find((vehicle) => vehicle.id === vehicleId)).filter(Boolean).map((vehicle) => ({
    organization_id: organizationId, assignment_date: assignmentDate, driver_id: driver.id, vehicle_id: vehicle.dbId, source: 'manual',
  }))
  if (assignments.length) resultOrThrow(await supabase.from('driver_vehicle_assignments').insert(assignments), '担当車両を保存できませんでした')
  return driver
}

export async function saveCloudVehicle(item, organizationId) {
  requireCloud()
  return resultOrThrow(await supabase.from('vehicles').insert({
    organization_id: organizationId, registration_number: item.number, vehicle_type: item.type, model: item.model || '', branch: item.branch, active: true,
  }).select('id, public_id').single(), '車両を保存できませんでした')
}

export async function saveCloudIntegrations(settings, organizationId) {
  requireCloud()
  const rows = Object.entries(settings.integrations || {}).map(([systemType, value]) => ({
    organization_id: organizationId,
    system_type: systemType,
    connection_method: integrationMethodToDb[value.method] || 'unset',
    endpoint_url: value.endpointUrl || null,
    enabled: Boolean(value.enabled),
  }))
  if (!rows.length) return []
  return resultOrThrow(await supabase.from('integration_settings').upsert(rows, { onConflict: 'organization_id,system_type' }).select(), '連携設定を保存できませんでした')
}
